import { useDialogAlert } from '@app/components/DialogAlert';
import InstallAppWarning, { useFetchStoryWithPromotionOrDefaultMessage } from '@app/components/InstallAppWarning';
import MenuItemDetailsModal from '@app/components/MenuItemDetailsModal';
import { MenuItemDetailsSession } from '@app/components/MenuItemDetailsModal/storage';
import ModalResponsive, { useModalWithHashingControl } from '@app/components/ModalResponsive';
import SearchBar from '@app/components/SearchBar';
import DIMENSIONS from '@app/config/dimensions';
import { useBagContext } from '@app/contexts/Bag';
import { useClientArea } from '@app/contexts/ClientArea';
import { useClock } from '@app/contexts/Clock';
import useTimeManager from '@app/hooks/use-time-manager';
import AppBar from '@app/pages/Restaurant/AppBar';
import Bag from '@app/pages/Restaurant/Bag';
import { useTopPosition } from '@app/pages/Restaurant/Bag/helpers';
import { CliqueiAcheiData, Menu, MenuItem, PageModels, Restaurant } from '@app/types';
import { MenuItemWithDetails, MenuItemWithDetailsOfBag } from '@app/types/menu-item';
import { useIsTabletOrMobileScreenSize } from '@app/utils/media-query';
import { MenuItemFactory } from '@app/utils/menu';
import { useQueryParamsWithNextRouter, WindowHistory } from '@app/utils/routing';
import { useTagManagerInstance } from '@app/utils/tag-manager';
import { Box, CircularProgress } from '@material-ui/core';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BagBottomBar from './BagBottomBar';
import ClientLoginIndicator from './ClientLoginIndicator';
import { RestaurantContext } from './context';
import FeedbackModal from './FeedbackModal';
import Footer from './Footer';
import Head from './Head';
import {
  getRestaurantColors,
  injectMenuItemSessionData,
  useAutoCliqueiAcheiDataUpdate,
  useFavoriteMenu,
  useFetchCliqueiAcheiData,
  useOnlyRegisterOrderOption,
  useRestaurantMenus,
  useRestaurantMockedData,
} from './helpers';
import LeadWarning from './LeadWarning';
import LoginModal from './LoginModal';
import MenuCardList from './MenuCardList';
import MenuScrollerSelector from './MenuScrollerSelector';
import MenusHorizontalScroller from './MenusHorizontalScroller';
import RestaurantInfo from './RestaurantInfo';
import ScheduleTable from './ScheduleTable';
import classes from './styles.module.css';
import { useEnrichedRestaurant } from './use-enriched-menus';
import { useSearchOnMenu } from './use-search-on-menu';
import useSetupAlerts from './use-setup-alerts';
import YoutubeVideoModal from './YoutubeVideoModal';

export type RestaurantProps = {
  isAInternalMenu: boolean;
  restaurant?: Restaurant;
  cliqueiAcheiData?: CliqueiAcheiData;
  initialProduct?: MenuItemWithDetails | null;
  shouldAddInitialProductToBag?: boolean;
  isLead?: boolean;
  errorCode?: number;
};

export default function RestaurantPage({
  restaurant: initialRestaurantData,
  cliqueiAcheiData: initialCliqueiAcheiData,
  initialProduct,
  shouldAddInitialProductToBag,
  isLead,
  errorCode,
  isAInternalMenu,
}: RestaurantProps) {
  const clock = useClock();
  const bag = useBagContext();
  const { isLoggedIn: isLoggedInClientArea, client } = useClientArea();

  const searchInputRef = useRef<HTMLInputElement>(null);

  const { counterNumber, tableNumber } = useQueryParamsWithNextRouter();
  const isOrderedOnRestaurant = Boolean(counterNumber || tableNumber);

  const [restaurant, setRestaurant] = useState(initialRestaurantData);

  const enrichedRestaurant = useEnrichedRestaurant(restaurant, clock.now());
  const restaurantMenus = useRestaurantMenus(enrichedRestaurant?.menus ?? [], {
    isAInternalMenu,
    isOrderedOnRestaurant,
    isLoggedInClientArea,
  });

  const [currentMenuActived, setCurrentMenuActived] = useState(restaurantMenus[0] ?? null);
  const [cliqueiAcheiData, setCliqueiAcheiData] = useState(initialCliqueiAcheiData);
  const [selectedMenuItem, setSelectedMenuItem] = useState<MenuItemWithDetails | null>(null);
  const [selectedMenuItemOfBagForEdit, setSelectedMenuItemOfBagForEdit] = useState<MenuItemWithDetailsOfBag | null>(
    null
  );
  const [selectedMenuItemHasInjectedSessionData, setSelectedMenuItemHasInjectedSessionData] = useState(false);
  const [isVisibleLoginModal, setIsVisibleLoginModal] = useState(false);
  const [shouldDisplayAppWarningOnBag, setShouldDisplayAppWarningOnBag] = useState(false);
  const [onlyRegisterOrder, setOnlyRegisterOrder] = useOnlyRegisterOrderOption(client?.ClienteId);
  const [displayYoutubeVideo, setDisplayYoutubeVideo] = useState(false);
  const [term, setTerm] = useState('');

  const { storyPromotion, defaultMessage, getStoryPreviewFromStoryId } = useFetchStoryWithPromotionOrDefaultMessage(
    cliqueiAcheiData
  );

  const isTabletOrMobile = useIsTabletOrMobileScreenSize();
  const timeManager = useTimeManager(cliqueiAcheiData?.Horarios, clock, {
    isAPublicMenu: !isAInternalMenu,
    isBreakTime: restaurant?.estaDeFerias ?? false,
  });
  const { dialogRef, DialogAlert } = useDialogAlert();

  useAutoCliqueiAcheiDataUpdate(initialCliqueiAcheiData, setCliqueiAcheiData);

  const isAInternalMenuOrOrderedOnRestaurant = Boolean(isAInternalMenu || isOrderedOnRestaurant);
  const shouldIgnoreSetupAlerts = Boolean(isAInternalMenuOrOrderedOnRestaurant || isLoggedInClientArea);
  useSetupAlerts(cliqueiAcheiData, timeManager, shouldIgnoreSetupAlerts, dialogRef);

  const { searchOnMenuResult, searchIsActived, isSearching } = useSearchOnMenu(restaurantMenus, {
    term,
    shouldIgnoreSearch: Boolean(isLead),
  });

  const { favoriteMenu, revalidateFavoriteMenu } = useFavoriteMenu(restaurantMenus);

  useFetchCliqueiAcheiData(cliqueiAcheiData?.Cod_Cliente, Boolean(isLead), setCliqueiAcheiData);

  const tagManager = useTagManagerInstance(cliqueiAcheiData);

  const isMenuOnlyView = Boolean(
    restaurant?.Configuracoes?.cardapioApenasParaVisualizacao ||
      (isOrderedOnRestaurant && restaurant?.Configuracoes?.desativarPedidosNaMesaEBalcao)
  );

  useRestaurantMockedData(restaurant, searchIsActived, setRestaurant);

  useEffect(() => {
    if (restaurant && cliqueiAcheiData) tagManager?.pushPageViewed(restaurant, cliqueiAcheiData);
  }, []);

  useEffect(() => {
    if (isLead) return;

    if (shouldAddInitialProductToBag && initialProduct && restaurant) {
      bag.clearItems();

      setTimeout(() => {
        if (!initialProduct) return;
        bag.addItem(initialProduct, {
          observations: '',
          amount: 1,
        });
        const url = `/local/${restaurant.cliqueiAcheiId}`;
        WindowHistory.replaceWithQueryParams(url);
      }, 500);
    }
  }, []);

  useEffect(() => {
    if (isLead || shouldAddInitialProductToBag || !initialProduct) return;

    const menuItemSessionData = MenuItemDetailsSession.getMenuItemDataPreviouslyFilled(initialProduct.id);
    if (menuItemSessionData) {
      initialProduct = injectMenuItemSessionData(initialProduct, menuItemSessionData);
      setSelectedMenuItemHasInjectedSessionData(true);
    }
    setSelectedMenuItem(initialProduct);
    WindowHistory.pushWithQueryParams(window.location.pathname);
    setTimeout(() => menuItemModal.open(), 200);
  }, []);

  useEffect(() => {
    if (!isTabletOrMobile && mobileBagModal.state) mobileBagModal.close();
  }, [isTabletOrMobile]);

  const menuItemModal = useModalWithHashingControl('menu-item');
  const mobileBagModal = useModalWithHashingControl('sacola', {
    onHashChange: (event) => {
      const shouldPreventMobileBagClose = event.oldURL.includes('#sacola') && event.newURL.search(/#[a-zA-Z]/i) !== -1;
      if (shouldPreventMobileBagClose) return true;
      return window.location.hash === '#sacola';
    },
  });
  const feedbackModal = useModalWithHashingControl('feedback');

  useEffect(() => {
    if (selectedMenuItemOfBagForEdit && !menuItemModal.state) menuItemModal.open();
  }, [selectedMenuItemOfBagForEdit]);

  const handleAddMenuItemClick = useCallback((menuItem: MenuItem, menu: Menu) => {
    const selected = MenuItemFactory.createMenuItemWithDetails({ menuItem, menu });
    const url = `/local/${(restaurant as Restaurant).cliqueiAcheiId}/produto/${menuItem.id}`;
    WindowHistory.replaceWithQueryParams(url);
    menuItemModal.open();
    setSelectedMenuItem(selected);
  }, []);

  function handleCloseMenuItemModal() {
    const url = `/local/${(restaurant as Restaurant).cliqueiAcheiId}`;
    menuItemModal.close();
    restartMenuItemDetailsModalStates();
    setTimeout(() => WindowHistory.replaceWithQueryParams(url), 300);
    revalidateFavoriteMenu();

    function restartMenuItemDetailsModalStates() {
      setSelectedMenuItem(null);
      setSelectedMenuItemOfBagForEdit(null);
      setSelectedMenuItemHasInjectedSessionData(false);
    }
  }

  function handleScrollToMenu(menu: Menu) {
    const menuElement = document.getElementById(`menu-card-${menu.id}`);
    setTimeout(() => {
      const offset =
        (menuElement?.offsetTop ?? 0) - DIMENSIONS.APP_BAR.HEIGHT - DIMENSIONS.MAIN_PAGE.SEARCH_AND_SCROLLER_BOX_HEIGHT;
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }, 200);
  }

  const handleReachedMenuCardOnTopOfScroll = useCallback((menu: Menu, _: number) => {
    setCurrentMenuActived((oldValue) => {
      if (oldValue?.id !== menu.id) return menu;
      return oldValue;
    });
  }, []);

  function handleRestrictAreaLoginClick() {
    setIsVisibleLoginModal(true);
  }

  function handleSeeBagClick() {
    mobileBagModal.open();
  }

  function handleFeedbackClick() {
    feedbackModal.open();
  }

  function handleYoutubeClick() {
    setDisplayYoutubeVideo(true);
  }

  function handleFeedbackEmailSended() {
    feedbackModal.close();
    dialogRef.current?.alert({
      title: 'Feedback enviado!',
      message: 'Sua mensagem foi enviada com sucesso. Agradecemos o feedback!',
    });
  }

  if (errorCode) {
    return <p>Ocorreu um erro - Code: {errorCode}</p>;
  }

  if (isLead) {
    return <LeadWarning whatsapp={restaurant?.whatsapp ?? ''} />;
  }

  const restaurantContextValue = {
    ...getRestaurantColors(cliqueiAcheiData as CliqueiAcheiData),
    setSelectedMenuItemOfBagForEdit,
    cliqueiAcheiData: cliqueiAcheiData as CliqueiAcheiData,
    restaurant: restaurant as Restaurant,
    timeManager,
    isAInternalMenu,
    isOrderedOnRestaurant,
    isAInternalMenuOrOrderedOnRestaurant,
    isMenuOnlyView,
    onlyRegisterOrder,
    tagManager,
  };

  const menus = useMemo(() => {
    if (searchIsActived) return searchOnMenuResult;
    if (favoriteMenu) return [favoriteMenu, ...restaurantMenus];
    return restaurantMenus;
  }, [searchIsActived, searchOnMenuResult, restaurantMenus, favoriteMenu]);

  const commonBagProps = {
    cliqueiAcheiData: cliqueiAcheiData as CliqueiAcheiData,
    restaurant: restaurant as Restaurant,
    onFavoritedItems: revalidateFavoriteMenu,
  };

  const kitchenYoutubeVideoId = restaurant?.Configuracoes?.youtubeVideoId;

  const hasMenusToDisplay = menus.length > 0;
  const paddingBottom = isTabletOrMobile && bag.items.length > 0 ? DIMENSIONS.BAG_BAR.HEIGHT : 0;
  const shouldDisplayDesktopMenuScrollerSelector = menus.length > 1;

  return (
    <RestaurantContext.Provider value={restaurantContextValue}>
      <Head cliqueiAcheiData={cliqueiAcheiData as CliqueiAcheiData} restaurant={restaurant as Restaurant} />
      <AppBar
        isBarrigaPageModel={cliqueiAcheiData?.ModeloPaginaId === PageModels.CardapioBarriga}
        onSearch={setTerm}
        onCloseSearch={() => setTerm('')}
        onFeedbackClick={handleFeedbackClick}
        onYoutubeClick={handleYoutubeClick}
        displayYoutubeButton={Boolean(kitchenYoutubeVideoId) && !isLoggedInClientArea}
        isLoggedInClientArea={isLoggedInClientArea}
      />
      <div className={classes.boxContain} style={{ paddingBottom }}>
        <div className={classes.container} style={{ padding: `${DIMENSIONS.APP_BAR.BOTTOM_SPACING}px 0 0 0` }}>
          <div className={classes.leftContain}>
            {isLoggedInClientArea && (
              <ClientLoginIndicator
                onlyRegisterOrder={onlyRegisterOrder}
                onOnlyRegisterOrderChange={setOnlyRegisterOrder}
              />
            )}

            {!isLoggedInClientArea && (
              <>
                <RestaurantInfo data={cliqueiAcheiData as CliqueiAcheiData} restaurant={restaurant as Restaurant} />
                {cliqueiAcheiData?.UsaAgenda && <ScheduleTable id={String(cliqueiAcheiData.Cod_Cliente)} />}
              </>
            )}

            {Boolean(hasMenusToDisplay || searchIsActived) && !isTabletOrMobile && (
              <div className={classes.fixedBox} style={{ height: DIMENSIONS.MAIN_PAGE.SEARCH_AND_SCROLLER_BOX_HEIGHT }}>
                {shouldDisplayDesktopMenuScrollerSelector && (
                  <MenuScrollerSelector
                    menus={menus}
                    activedMenu={currentMenuActived}
                    onSelectMenuToScroll={handleScrollToMenu}
                  />
                )}
                <SearchBar value={term} onChangeValue={setTerm} inputRef={searchInputRef} />
              </div>
            )}

            {Boolean(hasMenusToDisplay || searchIsActived) && isTabletOrMobile && (
              <MenusHorizontalScroller
                menus={menus}
                activedMenu={currentMenuActived}
                onSelectMenuToScroll={handleScrollToMenu}
              />
            )}

            {isSearching && (
              <Box display="flex" flexDirection="row" justifyContent="center" mt={5}>
                <CircularProgress size={30} style={{ color: restaurantContextValue.primaryColor }} />
              </Box>
            )}

            {!isSearching && hasMenusToDisplay && (
              <MenuCardList
                menus={menus}
                onAddMenuItemClick={handleAddMenuItemClick}
                onRechedTopOfScroll={handleReachedMenuCardOnTopOfScroll}
              />
            )}

            {!isSearching && !hasMenusToDisplay && (
              <>
                {searchIsActived && (
                  <Box display="flex" flexDirection="row" justifyContent="center" mt={5}>
                    <p>
                      Não encontramos <strong>{term.trim()}</strong> neste cardápio
                    </p>
                  </Box>
                )}
                {!searchIsActived && <MenuInMaintenanceWarn />}
              </>
            )}
            {isTabletOrMobile && (
              <Footer onLoginClick={handleRestrictAreaLoginClick} hiddenLoginButton={isLoggedInClientArea} />
            )}
          </div>
          <RightSideContainer>
            <Bag {...commonBagProps} />
            {bag.items.length === 0 && shouldDisplayAppWarningOnBag && Boolean(storyPromotion || defaultMessage) && (
              <InstallAppWarning
                storyPromotion={storyPromotion}
                message={defaultMessage}
                extraData={{ storyOwnerName: getStoryPreviewFromStoryId(storyPromotion?.FotoId ?? 0)?.Nome ?? '' }}
                useAsModal={false}
              />
            )}
          </RightSideContainer>
        </div>
        {!isTabletOrMobile && (
          <Footer onLoginClick={handleRestrictAreaLoginClick} hiddenLoginButton={isLoggedInClientArea} />
        )}
        {bag.items.length > 0 && isTabletOrMobile && (
          <BagBottomBar amount={bag.items.length} onSeeClick={handleSeeBagClick} />
        )}
      </div>
      {(selectedMenuItem || selectedMenuItemOfBagForEdit) && menuItemModal.state && (
        <MenuItemDetailsModal
          isVisible={(!!selectedMenuItem || !!selectedMenuItemOfBagForEdit) && menuItemModal.state}
          menuItem={selectedMenuItem ?? (selectedMenuItemOfBagForEdit as MenuItemWithDetailsOfBag)}
          menuItemHasSessionDataInjected={selectedMenuItemHasInjectedSessionData}
          onClose={handleCloseMenuItemModal}
        />
      )}

      <InstallAppWarning
        storyPromotion={storyPromotion}
        message={defaultMessage}
        extraData={{ storyOwnerName: getStoryPreviewFromStoryId(storyPromotion?.FotoId ?? 0)?.Nome ?? '' }}
        useAsModal
        onModalClosed={() => setShouldDisplayAppWarningOnBag(true)}
      />

      <LoginModal isVisible={isVisibleLoginModal} onClose={() => setIsVisibleLoginModal(false)} />

      <ModalResponsive
        open={mobileBagModal.state}
        onClose={() => mobileBagModal.close()}
        appBarConfig={{
          title: isAInternalMenuOrOrderedOnRestaurant ? 'Escolhas' : 'Sacola',
          color: restaurantContextValue.primaryColor,
          tintColor: restaurantContextValue.secondaryColor,
        }}
        hiddenDesktopBar
        desktopHeightContent={0}
        desktopWidthContent={0}
      >
        <Bag
          {...commonBagProps}
          onRequestCloseMobileModal={() => setTimeout(() => mobileBagModal.close(), 200)}
          shouldLogCheckoutEvent
        />
      </ModalResponsive>
      <FeedbackModal
        isVisible={feedbackModal.state}
        onClose={() => feedbackModal.close()}
        onEmailSended={handleFeedbackEmailSended}
      />
      {kitchenYoutubeVideoId && (
        <YoutubeVideoModal
          isVisible={displayYoutubeVideo}
          onClose={() => setDisplayYoutubeVideo(false)}
          videoId={kitchenYoutubeVideoId}
        />
      )}
      {DialogAlert}
    </RestaurantContext.Provider>
  );
}

function MenuInMaintenanceWarn() {
  return (
    <Box display="flex" flexDirection="column" style={{ marginTop: 20 }}>
      <p style={{ textAlign: 'center' }}>Cardápio em manutenção.</p>
      <p style={{ textAlign: 'center', fontSize: 12 }}>Tente novamente mais tarde.</p>
    </Box>
  );
}

function RightSideContainer({ children }: { children: ReactNode }) {
  const rightSideContainerRef = useRef<HTMLDivElement>(null);
  const rightSideTop = useTopPosition(rightSideContainerRef.current);

  return (
    <div ref={rightSideContainerRef} className={classes.rightContain} style={{ top: rightSideTop }}>
      {children}
    </div>
  );
}
